import { useState, useEffect, useCallback } from "react";
import { Search, ChevronLeft, ChevronRight, MailCheck } from "lucide-react";
import { getUsers, toggleUser, verifyUserEmail } from "../utils/api";

const TABS = [
  { id: "all", label: "All" },
  { id: "seeker", label: "Seekers" },
  { id: "recruiter", label: "Recruiters" },
  { id: "student", label: "Students" },
  { id: "unverified", label: "Unverified" },
];

const ROLE_BADGE = { seeker: "badge-blue", recruiter: "badge-purple", student: "badge-amber" };

function fmtDate(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
}

export default function Users() {
  const [tab, setTab] = useState("all");
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [busyId, setBusyId] = useState(null);

  const fetchUsers = useCallback(() => {
    setLoading(true);
    setError("");
    const params = { page };
    if (tab === "unverified") params.status = "unverified";
    else if (tab !== "all") params.role = tab;
    if (search) params.search = search;
    getUsers(params)
      .then((res) => setData(res.data))
      .catch((err) => setError(err.response?.data?.error || "Failed to load users"))
      .finally(() => setLoading(false));
  }, [tab, page, search]);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  const handleToggle = async (id) => {
    setBusyId(id);
    try {
      await toggleUser(id);
      fetchUsers();
    } catch (err) {
      setError(err.response?.data?.error || "Action failed");
    } finally { setBusyId(null); }
  };

  const handleVerify = async (id) => {
    setBusyId(id);
    try {
      await verifyUserEmail(id);
      fetchUsers();
    } catch (err) {
      setError(err.response?.data?.error || "Action failed");
    } finally { setBusyId(null); }
  };

  const statusBadge = (u) => {
    if (!u.email_verified) return <span className="badge badge-amber">Unverified</span>;
    return u.is_active
      ? <span className="badge badge-green">Active</span>
      : <span className="badge badge-red">Inactive</span>;
  };

  return (
    <div>
      <h1 className="page-title">Users</h1>
      <p className="page-sub">Manage seekers, recruiters and students.</p>

      {/* Search + tabs */}
      <div style={{
        display: "flex", gap: 12, flexWrap: "wrap",
        alignItems: "center", margin: "18px 0 14px"
      }}>
        <form
          onSubmit={(e) => { e.preventDefault(); setPage(1); setSearch(searchInput.trim()); }}
          style={{ position: "relative", flex: "1 1 260px", maxWidth: 360 }}
        >
          <Search size={15} style={{
            position: "absolute", left: 13, top: "50%",
            transform: "translateY(-50%)", color: "#a0aec0"
          }} />
          <input
            className="input" placeholder="Search by name or email…"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            style={{ paddingLeft: 36 }}
          />
        </form>
        <div className="tabs">
          {TABS.map((t) => (
            <button key={t.id}
              className={`tab ${tab === t.id ? "tab-active" : ""}`}
              onClick={() => { setTab(t.id); setPage(1); }}>
              {t.label}
            </button>
          ))}
        </div>
      </div>

      {error && <div className="error-box" style={{ marginBottom: 14 }}>{error}</div>}

      <div className="card table-wrap">
        {loading ? (
          <div className="empty"><span className="spinner spinner-dark" style={{ width: 26, height: 26 }} /></div>
        ) : !data?.users?.length ? (
          <div className="empty">No users found{search ? ` for “${search}”` : ""}.</div>
        ) : (
          <table className="table">
            <thead>
              <tr>
                <th>Name</th><th>Email</th><th>Role</th>
                <th>Status</th><th>Joined</th><th style={{ textAlign: "right" }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {data.users.map((u) => (
                <tr key={u._id}>
                  <td style={{ fontWeight: 600 }}>{u.name}</td>
                  <td style={{ color: "#718096" }}>{u.email}</td>
                  <td><span className={`badge ${ROLE_BADGE[u.role] || "badge-gray"}`}>{u.role}</span></td>
                  <td>{statusBadge(u)}</td>
                  <td style={{ color: "#718096", whiteSpace: "nowrap" }}>{fmtDate(u.created_at)}</td>
                  <td style={{ textAlign: "right", whiteSpace: "nowrap" }}>
                    {!u.email_verified && (
                      <button className="btn btn-green btn-sm" style={{ marginRight: 8 }}
                        disabled={busyId === u._id}
                        onClick={() => handleVerify(u._id)}>
                        <MailCheck size={13} /> Verify Email
                      </button>
                    )}
                    <button
                      className={`btn btn-sm ${u.is_active ? "btn-danger" : "btn-green"}`}
                      disabled={busyId === u._id}
                      onClick={() => handleToggle(u._id)}>
                      {busyId === u._id ? "…" : u.is_active ? "Deactivate" : "Activate"}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Pagination */}
      {data && data.pages > 1 && (
        <div style={{
          display: "flex", alignItems: "center", justifyContent: "space-between",
          marginTop: 14
        }}>
          <span style={{ fontSize: 13, color: "#718096" }}>
            {data.total} user{data.total !== 1 ? "s" : ""} · Page {data.page} of {data.pages}
          </span>
          <div style={{ display: "flex", gap: 8 }}>
            <button className="btn btn-ghost btn-sm" disabled={page <= 1}
              onClick={() => setPage((p) => p - 1)}>
              <ChevronLeft size={14} /> Prev
            </button>
            <button className="btn btn-ghost btn-sm" disabled={page >= data.pages}
              onClick={() => setPage((p) => p + 1)}>
              Next <ChevronRight size={14} />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
